import React, { useEffect, useState } from 'react';
import { ShieldAlert, XCircle, CheckCircle, Shield, Settings, Info, RefreshCw } from 'lucide-react';
import { apiService } from '../../services/apiService';
import { categoryService, Category } from '../../services/categoryService';
import { useToast } from '../../contexts/ToastContext';
import ScrollToTop from '../../components/Common/ScrollToTop';

interface AdminRequest {
  id: string;
  email: string;
  name: string;
  admin_status: 'pending' | 'approved' | 'rejected';
  admin_permissions: string[];
}

const PERMISSION_OPTIONS = [
  { id: 'tours', name: 'Safari Tours', desc: 'Create, modify, and delete safaris and pricing' },
  { id: 'categories', name: 'Categories', desc: 'Organize safaris into categories' },
  { id: 'featured-order', name: 'Featured Order', desc: 'Determine display order of featured packages' },
  { id: 'guides', name: 'Tour Guides', desc: 'Manage guides, profiles, and reviews' },
  { id: 'bookings', name: 'Bookings', desc: 'View bookings and update reservation details' },
  { id: 'custom-safaris', name: 'Custom Safaris', desc: 'Oversee tailored client custom safari requests' },
  { id: 'accommodations', name: 'Accommodations', desc: 'Add/modify partner hotels, lodges, and camps' },
  { id: 'groups', name: 'Shared Groups', desc: 'Coordinate group tours and departures' },
  { id: 'gifts', name: 'Gift Shop', desc: 'Manage gift products, categories, and orders' },
  { id: 'logistics', name: 'Accounting & Logistics', desc: 'Access revenue aggregates and logistical sheets' },
  { id: 'about', name: 'About Page', desc: 'Edit background information and company details' },
  { id: 'settings', name: 'System Settings', desc: 'Configure global contact details and WhatsApp numbers' }
];

const AdminRequests: React.FC = () => {
  const [admins, setAdmins] = useState<AdminRequest[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedAdmin, setSelectedAdmin] = useState<AdminRequest | null>(null);
  const [selectedPermissions, setSelectedPermissions] = useState<string[]>([]);
  const { showSuccess, showError } = useToast();

  const fetchCategories = async () => {
    try {
      const cats = await categoryService.getActiveCategories();
      setCategories(cats);
    } catch (err) {
      console.error('Failed to load categories', err);
    }
  };

  const fetchAdmins = async () => {
    setLoading(true);
    try {
      // The API service returns the parsed array directly
      const data = await apiService.getAdmins();
      setAdmins(data);
    } catch (err: any) {
      showError('Error', err.message || 'Failed to load administrator accounts');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAdmins();
    fetchCategories();
  }, []);

  const handleApprove = async (id: string) => {
    try {
      await apiService.updateAdminStatus(id, 'approved');
      showSuccess('Approved', 'Admin request approved successfully');
      fetchAdmins();
    } catch (err: any) {
      showError('Error', err.message || 'Failed to approve request');
    }
  };

  const handleReject = async (id: string) => {
    try {
      await apiService.updateAdminStatus(id, 'rejected');
      showSuccess('Rejected', 'Admin request rejected');
      fetchAdmins();
    } catch (err: any) {
      showError('Error', err.message || 'Failed to reject request');
    }
  };

  const handleRevoke = async (id: string) => {
    if (window.confirm('Are you sure you want to revoke this administrator\'s access? This will set their status to rejected.')) {
      try {
        await apiService.updateAdminStatus(id, 'rejected');
        showSuccess('Access Revoked', 'Administrator access has been revoked.');
        fetchAdmins();
      } catch (err: any) {
        showError('Error', err.message || 'Failed to revoke administrator access');
      }
    }
  };

  const openPermissionsModal = (admin: AdminRequest) => {
    setSelectedAdmin(admin);
    // Ensure 'overview' is preserved, it's default
    setSelectedPermissions(admin.admin_permissions || ['overview']);
  };

  const closePermissionsModal = () => {
    setSelectedAdmin(null);
    setSelectedPermissions([]);
  };

  const togglePermission = (permId: string) => {
    if (permId === 'tours') {
      // If unchecking tours, also uncheck all category specific permissions
      if (selectedPermissions.includes('tours')) {
        setSelectedPermissions(prev => prev.filter(p => p !== 'tours' && !p.startsWith('tours:')));
      } else {
        setSelectedPermissions(prev => [...prev, 'tours', 'tours:all']);
      }
      return;
    }

    if (permId.startsWith('tours:')) {
      setSelectedPermissions(prev => {
        let newPerms = [...prev];
        if (permId === 'tours:all') {
          if (newPerms.includes('tours:all')) {
            newPerms = newPerms.filter(p => p !== 'tours:all');
          } else {
            // checking all removes specific ones
            newPerms = newPerms.filter(p => !p.startsWith('tours:') || p === 'tours');
            newPerms.push('tours:all');
          }
        } else {
          // checking a specific category removes 'all'
          newPerms = newPerms.filter(p => p !== 'tours:all');
          if (newPerms.includes(permId)) {
            newPerms = newPerms.filter(p => p !== permId);
          } else {
            newPerms.push(permId);
          }
        }
        return newPerms;
      });
      return;
    }

    setSelectedPermissions(prev =>
      prev.includes(permId)
        ? prev.filter(p => p !== permId)
        : [...prev, permId]
    );
  };

  const handleSavePermissions = async () => {
    if (!selectedAdmin) return;

    try {
      // Keep overview as mandatory permission
      const finalPermissions = selectedPermissions.includes('overview')
        ? selectedPermissions
        : ['overview', ...selectedPermissions];

      await apiService.updateAdminPermissions(selectedAdmin.id, finalPermissions);
      showSuccess('Permissions Saved', `Updated permissions for ${selectedAdmin.name}`);
      closePermissionsModal();
      fetchAdmins();
    } catch (err: any) {
      showError('Error', err.message || 'Failed to update permissions');
    }
  };

  const pendingRequests = admins.filter(a => a.admin_status === 'pending');
  const activeAdmins = admins.filter(a => a.admin_status === 'approved' && a.email !== 'sighttrailsafaris@gmail.com');

  return (
    <div className="w-full bg-transparent font-sans pb-16">
      <ScrollToTop />

      <div className="flex items-center justify-between mb-8">
        <div className="flex items-center gap-3">
          <div className="p-3 bg-primary-100 dark:bg-primary-900/30 text-primary-600 rounded-2xl">
            <Shield className="w-6 h-6" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Admin Access Management</h1>
            <p className="text-sm text-gray-500 dark:text-gray-400">Review pending access requests and manage administrator system permissions</p>
          </div>
        </div>

        <button
          onClick={fetchAdmins}
          disabled={loading}
          className="flex items-center gap-2 px-4 py-2 bg-neutral-100 hover:bg-neutral-200 dark:bg-slate-800 dark:hover:bg-slate-700 text-gray-700 dark:text-gray-200 rounded-xl text-sm font-semibold transition"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          Refresh Data
        </button>
      </div>

      <div className="space-y-10">
        {/* SECTION 1: PENDING REQUESTS */}
        <div className="bg-white dark:bg-slate-800 rounded-3xl border border-neutral-200/60 dark:border-slate-700/50 shadow-sm overflow-hidden">
          <div className="px-6 py-5 border-b border-neutral-100 dark:border-slate-700/50 flex items-center justify-between">
            <h2 className="text-lg font-bold text-gray-900 dark:text-white flex items-center gap-2">
              <ShieldAlert className="w-5 h-5 text-amber-500" />
              Pending Requests ({pendingRequests.length})
            </h2>
          </div>

          <div className="p-6">
            {pendingRequests.length === 0 ? (
              <div className="text-center py-10">
                <Info className="w-8 h-8 text-neutral-300 dark:text-neutral-600 mx-auto mb-2" />
                <p className="text-neutral-500 dark:text-gray-400 text-sm font-medium">No pending registration requests.</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full border-collapse">
                  <thead>
                    <tr className="border-b border-neutral-100 dark:border-slate-700/30 text-xs font-bold text-neutral-400 dark:text-neutral-500 uppercase tracking-wider text-left">
                      <th className="pb-3 px-4">Name</th>
                      <th className="pb-3 px-4">Email</th>
                      <th className="pb-3 px-4 text-center">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-neutral-100 dark:divide-slate-700/30">
                    {pendingRequests.map(req => (
                      <tr key={req.id} className="text-sm text-gray-700 dark:text-gray-200">
                        <td className="py-4.5 px-4 font-semibold">{req.name}</td>
                        <td className="py-4.5 px-4 text-neutral-500 dark:text-slate-400 font-mono text-xs">{req.email}</td>
                        <td className="py-4.5 px-4">
                          <div className="flex items-center justify-center gap-2.5">
                            <button
                              onClick={() => handleApprove(req.id)}
                              className="inline-flex items-center gap-1.5 px-3.5 py-1.5 bg-green-500 hover:bg-green-600 text-white rounded-xl text-xs font-bold transition shadow-sm"
                            >
                              <CheckCircle className="w-3.5 h-3.5" /> Approve
                            </button>
                            <button
                              onClick={() => handleReject(req.id)}
                              className="inline-flex items-center gap-1.5 px-3.5 py-1.5 bg-red-500 hover:bg-red-600 text-white rounded-xl text-xs font-bold transition shadow-sm"
                            >
                              <XCircle className="w-3.5 h-3.5" /> Reject
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>

        {/* SECTION 2: APPROVED ADMINS */}
        <div className="bg-white dark:bg-slate-800 rounded-3xl border border-neutral-200/60 dark:border-slate-700/50 shadow-sm overflow-hidden">
          <div className="px-6 py-5 border-b border-neutral-100 dark:border-slate-700/50 flex items-center justify-between">
            <h2 className="text-lg font-bold text-gray-900 dark:text-white flex items-center gap-2">
              <Shield className="w-5 h-5 text-primary-500" />
              Active Admin Accounts ({activeAdmins.length})
            </h2>
          </div>

          <div className="p-6">
            {activeAdmins.length === 0 ? (
              <div className="text-center py-10">
                <Info className="w-8 h-8 text-neutral-300 dark:text-neutral-600 mx-auto mb-2" />
                <p className="text-neutral-500 dark:text-gray-400 text-sm font-medium">No other active admin accounts.</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full border-collapse">
                  <thead>
                    <tr className="border-b border-neutral-100 dark:border-slate-700/30 text-xs font-bold text-neutral-400 dark:text-neutral-500 uppercase tracking-wider text-left">
                      <th className="pb-3 px-4">Name</th>
                      <th className="pb-3 px-4">Email</th>
                      <th className="pb-3 px-4">Permissions</th>
                      <th className="pb-3 px-4 text-center">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-neutral-100 dark:divide-slate-700/30">
                    {activeAdmins.map(req => {
                      // Filter out 'overview' to avoid cluttering, but count it
                      const otherPerms = (req.admin_permissions || []).filter(p => p !== 'overview');
                      return (
                        <tr key={req.id} className="text-sm text-gray-700 dark:text-gray-200">
                          <td className="py-4.5 px-4 font-semibold">{req.name}</td>
                          <td className="py-4.5 px-4 text-neutral-500 dark:text-slate-400 font-mono text-xs">{req.email}</td>
                          <td className="py-4.5 px-4">
                            <div className="flex flex-wrap gap-1">
                              <span className="px-2 py-0.5 bg-neutral-100 dark:bg-slate-700 text-neutral-600 dark:text-slate-300 rounded-md text-[10px] font-bold uppercase tracking-wider">
                                Overview
                              </span>
                              {otherPerms.map(p => {
                                const isCategoryPerm = p.startsWith('tours:');
                                const label = isCategoryPerm 
                                  ? (p === 'tours:all' ? 'All Tour Categories' : `Tour Cat: ${categories.find(c => c.id === p.split(':')[1])?.name || 'Unknown'}`)
                                  : p.replace('-', ' ');

                                return (
                                  <span key={p} className={`px-2 py-0.5 rounded-md text-[10px] font-bold uppercase tracking-wider border ${
                                    isCategoryPerm 
                                      ? 'bg-blue-50 dark:bg-blue-950/30 text-blue-600 dark:text-blue-400 border-blue-100/30 dark:border-blue-900/20' 
                                      : 'bg-primary-50 dark:bg-primary-950/30 text-primary-600 dark:text-primary-400 border-primary-100/30 dark:border-primary-900/20'
                                  }`}>
                                    {label}
                                  </span>
                                );
                              })}
                              {otherPerms.length === 0 && (
                                <span className="text-neutral-400 dark:text-neutral-500 text-xs italic ml-1">No additional access</span>
                              )}
                            </div>
                          </td>
                          <td className="py-4.5 px-4">
                            <div className="flex items-center justify-center gap-2">
                              <button
                                onClick={() => openPermissionsModal(req)}
                                className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-neutral-100 hover:bg-neutral-200 dark:bg-slate-700 dark:hover:bg-slate-600 text-gray-700 dark:text-gray-200 rounded-xl text-xs font-bold transition border border-neutral-200/50 dark:border-slate-600/30"
                              >
                                <Settings className="w-3.5 h-3.5 text-neutral-500" /> Permissions
                              </button>
                              <button
                                onClick={() => handleRevoke(req.id)}
                                className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-red-50 hover:bg-red-100 text-red-600 rounded-xl text-xs font-bold transition border border-red-100"
                              >
                                <XCircle className="w-3.5 h-3.5" /> Revoke
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* PERMISSIONS MODAL */}
      {selectedAdmin && (
        <div className="fixed inset-0 z-50 flex items-center justify-center md:p-4">
          {/* Backdrop */}
          <div
            onClick={closePermissionsModal}
            className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm animate-fade-in"
          />

          {/* Modal Box */}
          <div className="relative w-full h-full md:h-auto max-w-2xl bg-white dark:bg-slate-800 md:rounded-[32px] shadow-2xl border-0 md:border border-neutral-100 dark:border-slate-700/80 overflow-hidden z-10 md:max-h-[85vh] flex flex-col animate-scale-up">
            {/* Header */}
            <div className="px-5 md:px-8 py-5 md:py-6 border-b border-neutral-100 dark:border-slate-700/60 flex-shrink-0 bg-white dark:bg-slate-800 relative z-20">
              <h3 className="text-xl font-bold text-gray-900 dark:text-white">Configure Admin Access</h3>
              <p className="text-sm text-neutral-500 dark:text-slate-400 mt-1">
                Setting page permissions for <span className="font-semibold text-primary-600 dark:text-primary-400">{selectedAdmin.name}</span> ({selectedAdmin.email})
              </p>
            </div>

            {/* Checkboxes List */}
            <div className="px-5 md:px-8 py-5 md:py-6 overflow-y-auto space-y-4 flex-1 h-full relative z-10">
              <div className="p-4 bg-amber-50/50 dark:bg-amber-950/20 border border-amber-100/50 dark:border-amber-900/30 rounded-2xl text-xs text-amber-700 dark:text-amber-300 font-medium flex gap-2">
                <Info className="w-4 h-4 flex-shrink-0 mt-0.5" />
                <span>By default, every administrator has access to the <strong>Overview Dashboard</strong>. Select from the tabs below to grant access to additional features.</span>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 pt-2">
                {PERMISSION_OPTIONS.map(opt => {
                  const isChecked = selectedPermissions.includes(opt.id);
                  return (
                    <div key={opt.id} className="flex flex-col">
                      <div
                        onClick={() => togglePermission(opt.id)}
                        className={`p-4 rounded-2xl border cursor-pointer transition-all flex gap-3 items-start select-none ${
                          isChecked
                            ? 'border-primary-500 bg-primary-50/30 dark:bg-primary-950/15'
                            : 'border-neutral-200/70 hover:border-neutral-300 dark:border-slate-700 dark:hover:border-slate-600 bg-white dark:bg-slate-800'
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={isChecked}
                          onChange={() => {}} // Handled by outer click
                          className="mt-1 accent-primary-600 h-4 w-4 rounded border-gray-300 focus:ring-primary-500 pointer-events-none"
                        />
                        <div>
                          <h4 className="text-sm font-bold text-gray-900 dark:text-white">{opt.name}</h4>
                          <p className="text-xs text-neutral-400 dark:text-neutral-500 mt-0.5 leading-relaxed">{opt.desc}</p>
                        </div>
                      </div>

                      {/* Sub-categories for Tours */}
                      {opt.id === 'tours' && isChecked && categories.length > 0 && (
                        <div className="mt-2 ml-8 pl-4 border-l-2 border-primary-200 dark:border-primary-800 space-y-2">
                          <div className="text-xs font-bold text-neutral-500 dark:text-neutral-400 mb-2 uppercase tracking-wider">
                            Restrict Access to Categories:
                          </div>
                          <label className="flex items-center gap-2 cursor-pointer group">
                            <input
                              type="checkbox"
                              checked={selectedPermissions.includes('tours:all')}
                              onChange={() => togglePermission('tours:all')}
                              className="accent-primary-600 h-3.5 w-3.5 rounded border-gray-300 focus:ring-primary-500"
                            />
                            <span className="text-xs font-medium text-gray-700 dark:text-gray-300 group-hover:text-primary-600 transition-colors">
                              All Categories
                            </span>
                          </label>
                          {categories.map(category => (
                            <label key={category.id} className="flex items-center gap-2 cursor-pointer group">
                              <input
                                type="checkbox"
                                checked={selectedPermissions.includes(`tours:${category.id}`)}
                                onChange={() => togglePermission(`tours:${category.id}`)}
                                className="accent-primary-600 h-3.5 w-3.5 rounded border-gray-300 focus:ring-primary-500"
                              />
                              <span className="text-xs font-medium text-gray-700 dark:text-gray-300 group-hover:text-primary-600 transition-colors">
                                {category.name}
                              </span>
                            </label>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Footer Buttons */}
            <div className="px-5 md:px-8 py-4 md:py-5 border-t border-neutral-100 dark:border-slate-700/60 bg-neutral-50 dark:bg-slate-800/90 flex items-center justify-end gap-3 flex-shrink-0 relative z-20">
              <button
                onClick={closePermissionsModal}
                className="px-5 py-2.5 bg-white dark:bg-slate-700 hover:bg-neutral-100 dark:hover:bg-slate-600 text-gray-700 dark:text-gray-200 border border-neutral-200 dark:border-slate-600 rounded-2xl text-sm font-bold transition"
              >
                Cancel
              </button>
              <button
                onClick={handleSavePermissions}
                className="px-6 py-2.5 bg-primary-600 hover:bg-primary-700 text-white rounded-2xl text-sm font-bold shadow-lg shadow-primary-900/15 transition hover:scale-[1.02] active:scale-[0.98]"
              >
                Save Permissions
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminRequests;
